import styled, { createGlobalStyle } from 'styled-components';
import theme from 'styled-theming';

const backgroundColorThead = theme('name', {
  light: 'rgba(242,242,242,255)',
  dark: 'rgba(70,70,70,255)',
});

const backgroundColorTh = theme('name', {
  light: 'rgb(242, 242, 242)',
  dark: '#575757',
});

const cardBorder = theme('name', {
  light: 'rgba(28, 28, 28, 0.12)',
  dark: 'rgba(255, 255, 255, 0.15)',
});

const tableBorder = theme('name', {
  light: 'rgba(28, 28, 28, 0.14)',
  dark: '#504945',
});

const blockBackground = theme('name', {
  light: '#f7f7f7',
  dark: 'rgba(255, 255, 255, 0.06)',
});

const blockBorderColor = theme('name', {
  light: 'rgba(28, 28, 28, 0.14)',
  dark: 'rgba(255, 255, 255, 0.16)',
});

const blockTextColor = theme('name', {
  light: '#1c1c1c',
  dark: '#f3f3f3',
});

export const ExternalInboxContainer = styled.div`
  /* Keep sticky headers and filters below modal backdrops without changing their local order. */
  isolation: isolate;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  overflow: hidden;

  /* ── Filters card ── */
  .filters-card {
    background: ${(props) => props.theme.background.primary};
    border-radius: 4px;
    border: 1px solid ${cardBorder};
    box-shadow:
      rgba(0, 0, 0, 0.28) 0 1px 3px,
      rgba(0, 0, 0, 0.2) 0 0 1px;
    padding: 10px 15px;
    margin-bottom: 12px;
    overflow: visible;
    flex-shrink: 0;
  }

  .filters-row {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    align-items: flex-end;
  }

  .filter-field {
    display: flex;
    flex-direction: column;
    gap: 4px;
    position: relative;
    z-index: 50;
    flex-shrink: 0;
  }

  .filter-label {
    font-size: 11px;
    font-weight: 600;
    color: ${(props) => props.theme.text.medium};
    white-space: nowrap;
  }

  .filters-refresh {
    align-self: flex-end;
    flex-shrink: 0;
    margin-left: auto;
  }

  /* ── Table wrapper ── */
  .table-wrapper {
    flex: 1;
    min-height: 0;
    position: relative;
    display: flex;
    flex-direction: column;
  }

  .table-container {
    border-radius: 5px 5px 0 0;
    flex: 1;
    min-height: 0;
    overflow: auto;
    scrollbar-width: auto;
    transition: opacity 0.15s ease;

    &.table-loading {
      opacity: 0.4;
      pointer-events: none;
    }

    &::-webkit-scrollbar {
      width: 14px;
    }
    &::-webkit-scrollbar-track {
      background: transparent;
    }
    &::-webkit-scrollbar-thumb {
      background-color: ${(props) => props.theme.border.medium};
      border-radius: 5px;
      border: 2px solid transparent;
      background-clip: padding-box;
    }
    &::-webkit-scrollbar-thumb:hover {
      background-color: ${(props) => props.theme.border.strong};
    }
  }

  .shell-table {
    width: 100%;
    min-width: 1360px;
    table-layout: fixed;

    /* Collapsed borders detach from sticky cells and let rows show through */
    border-collapse: separate;
    border-spacing: 0;

    thead {
      position: sticky;
      top: 0;
      z-index: 8;
      background-color: ${backgroundColorThead};
    }

    thead th {
      position: sticky;
      top: 0;
      z-index: 9;
      padding: 6px 8px;
      background-color: ${backgroundColorTh};
      border-top: 1px solid ${tableBorder};
      border-right: 1px solid ${tableBorder};
      border-bottom: 1px solid ${tableBorder};
      font-weight: 700;
      white-space: nowrap;
      user-select: none;
      text-align: left;
      vertical-align: middle;
      overflow: visible;
    }

    .col-status {
      width: 72px;
      text-align: center;
    }
    thead th.col-status {
      padding: 6px 4px;
      font-size: 12px;
    }
    tbody td.col-status {
      padding: 5px 4px;
    }
    .col-datetime {
      width: 150px;
    }
    .col-period {
      width: 190px;
    }
    .col-party {
      width: 140px;
    }
    .col-source {
      width: 100px;
    }
    .col-message-type {
      width: 160px;
    }
    .col-view {
      width: 64px;
      text-align: center;
    }

    tbody td {
      padding: 5px 8px;
      border-right: 1px solid ${tableBorder};
      border-bottom: 1px solid ${tableBorder};
      font-size: 13px;
      vertical-align: middle;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    thead th:first-child,
    tbody td:first-child {
      border-left: 1px solid ${tableBorder};
    }
  }

  .empty-cell {
    text-align: center;
    padding: 20px !important;
    color: ${(props) => props.theme.text.medium};
  }

  .load-sentinel {
    height: 1px;
  }

  .loading-more {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 20;
    display: flex;
    justify-content: center;
    align-items: center;
    pointer-events: none;
  }

  .sort-icon {
    opacity: 0.45;
    flex-shrink: 0;
  }

  .status-icon {
    align-items: center;
    justify-content: center;
    vertical-align: middle;
  }

  .th-sort-trigger {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    cursor: pointer;
    user-select: none;
    background: none;
    border: none;
    padding: 0;
    font: inherit;
    color: inherit;
    text-align: left;

    &:hover .sort-icon {
      opacity: 1;
    }
  }

  /* ── Row count footer ── */
  .row-count {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 8px;
    font-size: 12px;
    color: ${(props) => props.theme.text.medium};
    border-top: 1px solid ${tableBorder};
  }

  .row-count-loaded {
    margin-left: auto;
  }

  .row-count-total {
    font-weight: 600;
    color: ${(props) => props.theme.text.onSurface};
  }

  .row-count-sep {
    color: ${(props) => props.theme.text.medium};
  }
`;

// Modal and tooltips render via portal, so their styles must be global
export const ExternalInboxModalGlobalStyle = createGlobalStyle`
  .external-inbox-status-tooltip {
    padding: 8px 10px;
    max-width: 320px;
    line-height: 1.35;
    white-space: normal;
    word-break: break-word;
  }

  .external-inbox-modal .shell-modal-container {
    margin: 4vh auto 0;
    max-width: 92vw;
  }

  .external-inbox-modal .shell-modal-container-content {
    max-height: 92vh;
    overflow: hidden;
  }

  .external-inbox-modal-body {
    padding: 8px 0;
  }

  .external-inbox-reference {
    font-size: 14px;
    text-align: left;
    margin-bottom: 12px;
    color: ${blockTextColor};

    strong {
      font-weight: 700;
    }
  }

  .external-inbox-blocks-loading {
    display: flex;
    justify-content: center;
    padding: 24px 0;
  }

  .external-inbox-block-count {
    font-size: 12px;
    font-weight: 600;
    margin-bottom: 8px;
    color: ${blockTextColor};
  }

  .external-inbox-block-tabs {
    margin-top: 4px;
  }

  .external-inbox-block {
    border: 1px solid ${blockBorderColor};
    border-radius: 4px;
    background: ${blockBackground};
    color: ${blockTextColor};
    padding: 8px 10px;
  }

  .external-inbox-block-time {
    font-size: 12px;
    font-weight: 600;
    margin-bottom: 6px;
  }

  /* Fixed height keeps the modal steady when switching tabs; ~75% of the viewport */
  .external-inbox-block-content {
    margin: 0;
    font-size: 12px;
    white-space: pre;
    height: calc(92vh - 280px);
    min-height: 240px;
    overflow: auto;
  }

  .external-inbox-blocks-empty {
    text-align: center;
    padding: 20px;
    color: ${blockTextColor};
  }
`;
